<template>
  <div class="onboarding-wizard">
    <!-- Header -->
    <h2 class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2" style="color: var(--text-color)">Publisher Onboarding</h2>

    <!-- Progress Bar -->
    <div class="wizard-progress mb-4">
      <div class="progress-steps">
        <div
          v-for="step in totalSteps"
          :key="step"
          class="progress-step"
          :class="{
            active: currentStep === step,
            completed: currentStep > step,
          }"
          @click="jumpToStep(step)"
        >
          <div class="step-number tw-pt-[1px]">
            <i v-if="currentStep > step" class="pi pi-check"></i>
            <span v-else>{{ step }}</span>
          </div>
          <div class="step-label">{{ getStepLabel(step) }}</div>
        </div>
      </div>
      <div class="progress-bar-container">
        <div class="progress-bar" :style="{ width: `${progress}%` }"></div>
      </div>
    </div>

    <!-- Current Step Component -->
    <div class="wizard-content">
      <component
        :is="currentStepComponent"
        v-bind="currentStepProps"
        @next="handleNext"
        @back="handleBack"
        @cancel="handleCancel"
        @finish="handleFinish"
      />
    </div>

    <!-- Cancel Confirmation Dialog -->
    <Dialog
      v-model:visible="showCancelDialog"
      modal
      header="Confirm Cancel"
      :style="{ width: '450px' }"
    >
      <p>Are you sure you want to cancel? Any unsaved changes will be lost.</p>
      <template #footer>
        <Button
          label="No, Continue"
          @click="showCancelDialog = false"
          class="p-button-text"
        />
        <Button label="Yes, Cancel" @click="confirmCancel" severity="danger" />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "primevue/usetoast";
import Dialog from "primevue/dialog";
import Button from "primevue/button";

// Import step components
import Step1GroupDetails from "./Step1GroupDetails.vue";
import Step2SellerCreation from "./Step2SellerCreation.vue";
import Step3Summary from "./Step3Summary.vue";

const router = useRouter();
const toast = useToast();

// State
const currentStep = ref(1);
const totalSteps = 3;
const showCancelDialog = ref(false);

// Wizard data
const wizardData = ref({
  groupData: null,
  groupId: null,
  sellersData: [],
  groupMode: "new",
});

// Step labels
const stepLabels = {
  1: "Group Details",
  2: "Seller Creation",
  3: "Summary & Export",
};

// Computed
const progress = computed(() => {
  return ((currentStep.value - 1) / (totalSteps - 1)) * 100;
});

const currentStepComponent = computed(() => {
  const components = {
    1: Step1GroupDetails,
    2: Step2SellerCreation,
    3: Step3Summary,
  };
  return components[currentStep.value];
});

const currentStepProps = computed(() => {
  if (currentStep.value === 1) {
    return {
      data: wizardData.value.groupData,
      mode: wizardData.value.groupMode,
    };
  } else if (currentStep.value === 2) {
    return {
      groupId: wizardData.value.groupId,
      groupName: wizardData.value.groupData?.name || "Unknown",
      groupData: wizardData.value.groupData, // Pass groupData for video logic
      data: wizardData.value.sellersData,
    };
  } else if (currentStep.value === 3) {
    return {
      group: wizardData.value.groupData,
      groupId: wizardData.value.groupId,
      groupMode: wizardData.value.groupMode,
      sellers: wizardData.value.sellersData,
    };
  }
  return {};
});

// Methods
const getStepLabel = (step) => {
  return stepLabels[step] || `Step ${step}`;
};

const jumpToStep = (stepNumber) => {
  // Allow jumping back, but not forward
  if (stepNumber < currentStep.value) {
    currentStep.value = stepNumber;
  }
};

const handleNext = (data) => {
  if (currentStep.value === 1) {
    // Save group data
    wizardData.value.groupId = data.groupId;
    wizardData.value.groupData = data.groupData;
    wizardData.value.groupMode = data.groupMode || "new";
  } else if (currentStep.value === 2) {
    // Save sellers data
    wizardData.value.sellersData = data;
  }

  // Move to next step
  if (currentStep.value < totalSteps) {
    currentStep.value++;
  }
};

const handleBack = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

const handleCancel = () => {
  showCancelDialog.value = true;
};

const confirmCancel = () => {
  showCancelDialog.value = false;
  router.push("/admin/groups");
};

const handleFinish = () => {
  toast.add({
    severity: "success",
    summary: "Onboarding Complete",
    detail: "Publisher onboarding has been completed successfully!",
    life: 4000,
  });

  // Redirect after a short delay to show the toast
  setTimeout(() => {
    router.push("/admin/groups");
  }, 1000);
};
</script>

<style scoped>
.onboarding-wizard {
  padding: 20px;
}

.wizard-title {
  margin-top: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

/* Progress Bar Styles */
.wizard-progress {
  margin-bottom: 2rem;
}

.progress-steps {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  cursor: pointer;
  position: relative;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--surface-100);
  color: var(--text-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 0.5rem;
  border: 2px solid var(--surface-300);
  transition: all 0.3s ease;
}

.progress-step.active .step-number {
  background: var(--primary-color);
  color: var(--primary-color-text);
  border-color: var(--primary-color);
  transform: scale(1.1);
}

.progress-step.completed .step-number {
  background: var(--green-500);
  color: white;
  border-color: var(--green-500);
}

.step-label {
  font-size: 0.75rem;
  text-align: center;
  color: var(--text-color-secondary);
  padding: 1rem;
}

.progress-step.active .step-label {
  color: var(--primary-color);
  font-weight: 600;
}

.progress-step.completed .step-label {
  color: var(--text-color);
}

.progress-bar-container {
  width: 100%;
  height: 4px;
  background: var(--surface-200);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: var(--primary-color);
  transition: width 0.3s ease;
}

/* Content */
.wizard-content {
  min-height: 400px;
  margin-bottom: 2rem;
}

/* Responsive */
@media (max-width: 768px) {
  .step-label {
    font-size: 0.65rem;
  }

  .step-number {
    width: 32px;
    height: 32px;
    font-size: 0.875rem;
  }
}
</style>
